import {
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Button,
  IconButton,
  TextField,
  Tabs,
  Tooltip,
  Stack,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import { useStateContext } from "contexts/ContextProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid2";
import api from "services/api";
import { useWebSocket } from "contexts/WebSocketContext";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useAuth } from "../../../contexts/AuthContext";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import CustomDateSelector from "components/CustomDateSelector";
import { DataGrid, gridClasses, GridToolbar } from "@mui/x-data-grid";
import CustomTab from "components/CustomTab";
import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import { useMemo } from "react";
import ClearIcon from "@mui/icons-material/ClearRounded";
import { usePageTitle } from "hooks/usePageTitle";
import { numericComparator } from "helpers/ServiceHelpers";

const INVOICE_NUMBER_REFILL_START = 2100;

const InvoiceNew = () => {
  const { currentUser } = useAuth();

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { setOverlayData, setOverlayName, triggerPageRefresh } =
    useStateContext();

  const { socket } = useWebSocket();

  usePageTitle("Invoice New/Edit");

  const [isSavingChanges, setIsSavingChanges] = useState(false);

  dayjs.extend(durationPlugin);

  const handleOpenNotesDialog = (notes) => {
    const content = (
      <Stack spacing={2}>
        {notes.map((note) => (
          <Box
            key={note.id}
            sx={{
              p: 1.5,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.default,
              display: "flex",
              justifyContent: "space-between",
              alignItems: note.Member?.Name ? "flex-start" : "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: note.Member?.Name ? "flex-start" : "center",
              }}
            >
              {note.Member?.Name && (
                <Typography variant="h6">{note.Member.Name}</Typography>
              )}
              <Typography variant="h5">{note.Note}</Typography>
            </Box>

            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(note.Note)}
              sx={{ alignSelf: "center" }}
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Stack>
    );

    openDialog({
      title: "Service Notes",
      message: null, // not needed if you only show customContent
      customContent: content,
      secondaryAction: "Close",
      secondaryOnClick: closeDialog,
      theme: theme,
    });
  };

  const handleTabsChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [tabValue, setTabValue] = React.useState(0);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const currentMonth = dayjs().format("MMM");

  const [selectedMonths, setSelectedMonths] = useState([currentMonth]);
  const [tempSelectedMonths, setTempSelectedMonths] = useState([currentMonth]);

  // Year selector data
  const currentYear = dayjs().year();

  const [minMaxDates, setMinMaxDates] = useState(null);
  const [yearOptions, setYearOptions] = useState([String(currentYear)]);

  const [selectedYear, setSelectedYear] = useState(yearOptions);
  const [tempSelectedYear, setTempSelectedYear] = useState(yearOptions);

  // Fetch service date range on component mount
  useEffect(() => {
    const fetchDateRange = () => {
      api
        .get("/dashboard/serviceDateRange")
        .then((response) => {
          setMinMaxDates(response.data);

          // Generate year options from start year to end year
          if (response.data?.startDate?.year && response.data?.endDate?.year) {
            const startYear = response.data.startDate.year;
            const endYear = response.data.endDate.year;
            const years = [];

            for (let year = endYear; year >= startYear; year--) {
              years.push(String(year));
            }

            setYearOptions(years);
            setSelectedYear([years[0]]); // Set to most recent year
            setTempSelectedYear([years[0]]);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch service date range:", error);
          // Fall back to current year
          setYearOptions([String(currentYear)]);
        });
    };

    fetchDateRange();
  }, [currentYear]);

  // Generate year options when minMaxDates changes
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;

      // Create an array of years between startYear and endYear (inclusive)
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }

      setYearOptions(years);

      // If the current selected year is not in the range, update it to the current year
      // or the closest year in the range
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          // Choose the closest year in range to the current year
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }

          setSelectedYear([newYear]);
          setTempSelectedYear([newYear]);
        }
      }
    }
  }, [minMaxDates, currentYear, selectedYear]);

  // Handle date selection changes (combined months and years)
  const handleDateSelectionSave = (newSelection) => {
    // Ensure only one year is selected
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1), // Take only the last selected year
    };

    // Update state
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);

    // Fetch data
    fetchServicesData(updatedSelection.months, updatedSelection.years);
  };

  const [isLoading, setIsLoading] = useState(true);
  const [listOfServices, setListOfServices] = useState(null);

  const handleSelectionChange = (ids) => {
    const selectedIDs = new Set(ids);
    const selectedServices = listOfServices.filter((service) =>
      selectedIDs.has(service.id),
    );
    setSelectedServices(selectedServices);
  };

  const [selectedServices, setSelectedServices] = useState([]);

  const [listOfInvoices, setListOfInvoices] = useState([]);
  const [listOfInvoicedProjects, setListOfInvoicedProjects] = useState([]);
  const [existingInvoiceNumbers, setExistingInvoiceNumbers] = useState([]);

  const getClientPaymentMethods = useCallback((client) => {
    return Array.isArray(client?.ClientPaymentMethods)
      ? client.ClientPaymentMethods
      : [];
  }, []);

  const getClientPaymentMethodEntry = useCallback(
    (client, paymentMethodId) => {
      if (!paymentMethodId) {
        return null;
      }

      const selectedMethodId = Number(paymentMethodId);
      if (Number.isNaN(selectedMethodId)) {
        return null;
      }

      return getClientPaymentMethods(client).find((clientPaymentMethod) => {
        const candidateMethodId = Number(
          clientPaymentMethod?.PaymentMethodID || clientPaymentMethod?.PaymentMethod?.id,
        );
        return candidateMethodId === selectedMethodId;
      });
    },
    [getClientPaymentMethods],
  );

  const getPaymentMethodDisplayName = useCallback(
    (client, paymentMethodId) => {
      const methodEntry = getClientPaymentMethodEntry(client, paymentMethodId);
      return methodEntry?.PaymentMethod?.Name || "";
    },
    [getClientPaymentMethodEntry],
  );

  const isCreditCardPaymentMethodName = useCallback((name) => {
    return typeof name === "string" && name.trim().toLowerCase() === "credit card";
  }, []);

  const getDefaultPaymentMethodId = useCallback(
    (client) => {
      const methods = getClientPaymentMethods(client);
      if (methods.length === 0) {
        return null;
      }

      const firstMethod = methods[0];
      return firstMethod?.PaymentMethodID || firstMethod?.PaymentMethod?.id || null;
    },
    [getClientPaymentMethods],
  );

  const normalizeInvoiceNote = useCallback((note) => {
    if (typeof note !== "string") {
      return "";
    }

    return note.trim();
  }, []);

  const getDefaultInvoiceNoteForClient = useCallback(
    (client) => normalizeInvoiceNote(client?.InvoiceNoteTemplate),
    [normalizeInvoiceNote],
  );

  const getDefaultInvoiceNoteForPaymentMethod = useCallback(
    (client, paymentMethodId) => {
      const methodEntry = getClientPaymentMethodEntry(client, paymentMethodId);
      const methodNote = methodEntry?.DefaultInvoiceNote;
      if (typeof methodNote === "string" && methodNote.trim().length > 0) {
        return normalizeInvoiceNote(methodNote);
      }
      return getDefaultInvoiceNoteForClient(client);
    },
    [getClientPaymentMethodEntry, getDefaultInvoiceNoteForClient, normalizeInvoiceNote],
  );

  const buildInvoicePaymentDefaults = useCallback(
    (client) => {
      const paymentMethodId = getDefaultPaymentMethodId(client);
      const defaultInvoiceNote = getDefaultInvoiceNoteForPaymentMethod(
        client,
        paymentMethodId,
      );

      return {
        PaymentMethodID: paymentMethodId,
        Note: defaultInvoiceNote,
        NoteAutoApplied: defaultInvoiceNote.length > 0,
      };
    },
    [getDefaultInvoiceNoteForPaymentMethod, getDefaultPaymentMethodId],
  );

  const clientHasCreditCardPaymentMethod = useCallback((client) => {
    const methods = getClientPaymentMethods(client);
    if (methods.length === 0) return false;

    return methods.some((clientPaymentMethod) => {
      const name = clientPaymentMethod?.PaymentMethod?.Name;
      return isCreditCardPaymentMethodName(name);
    });
  }, [getClientPaymentMethods, isCreditCardPaymentMethodName]);

  const invoiceRequiresPaymentLink = useCallback(
    (invoice) => {
      if (invoice?.PaymentMethodID) {
        const selectedMethodName = getPaymentMethodDisplayName(
          invoice?.Client,
          invoice.PaymentMethodID,
        );

        if (selectedMethodName) {
          return isCreditCardPaymentMethodName(selectedMethodName);
        }
      }

      return clientHasCreditCardPaymentMethod(invoice?.Client);
    },
    [
      clientHasCreditCardPaymentMethod,
      getPaymentMethodDisplayName,
      isCreditCardPaymentMethodName,
    ],
  );

  const invoiceHasPaymentLink = useCallback((invoice) => {
    const paymentLink = invoice?.PaymentLink;
    return typeof paymentLink === "string" && paymentLink.trim().length > 0;
  }, []);

  const invoicesMissingPaymentLinks = useMemo(
    () =>
      (listOfInvoices || []).filter(
        (invoice) =>
          invoiceRequiresPaymentLink(invoice) && !invoiceHasPaymentLink(invoice),
      ),
    [invoiceHasPaymentLink, invoiceRequiresPaymentLink, listOfInvoices],
  );

  const isSaveBlockedByPaymentLinks = invoicesMissingPaymentLinks.length > 0;

  // Menu state for options button
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showPrivatePersons, setShowPrivatePersons] = useState(false);

  const { openDialog, handleErrorDialog, closeDialog, showSnackbar } =
    useStateContext();

  const handleErrorDialogRef = useRef(handleErrorDialog);

  useEffect(() => {
    handleErrorDialogRef.current = handleErrorDialog;
  }, [handleErrorDialog]);

  const [nextInvoiceNumber, setNextInvoiceNumber] = useState(1);

  // Global filter state management - stores the currently open filter field
  const [openProjectsFilter, setOpenProjectsFilter] = useState(null);
  const [openInvoicesFilter, setOpenInvoicesFilter] = useState(null);
  const [projectsSortModel, setProjectsSortModel] = useState([]);
  const [invoicesSortModel, setInvoicesSortModel] = useState([]);

  const handleProjectsSortModelChange = (newSortModel) => {
    //setProjectsSortModel(newSortModel);
  };

  const handleInvoicesSortModelChange = (newSortModel) => {
    //setInvoicesSortModel(newSortModel);
  };

  // Column definitions for filtering
  const projectsFilterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (_, row) => row.ProjectNumber || "N/A",
      },
      {
        field: "Date",
        valueGetter: (_, row) =>
          row.Date ? dayjs.tz(row.Date).format("M/D/YYYY") : "N/A",
      },
      {
        field: "Client",
        valueGetter: (_, row) => row.Client?.Account || "N/A",
      },
      { field: "Details", valueGetter: (_, row) => row.Details || "N/A" },
      {
        field: "Status",
        valueGetter: (_, row) => {
          switch (row.ServiceCategory) {
            case "Interpretation":
              return row?.InterpretationStatus || "N/A";
            case "Translation":
              return row?.TranslationStatus || "N/A";
            case "Other":
              return row?.OtherServiceStatus || "N/A";
            default:
              return "N/A";
          }
        },
      },
      {
        field: "AccountCharge",
        valueGetter: (_, row) => {
          const data = row.AccountCharge;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
          }
          return "N/A";
        },
      },
      {
        field: "ServiceCategory",
        valueGetter: (_, row) => row.ServiceCategory || "N/A",
      },
      {
        field: "ServiceNotes",
        valueGetter: (_, row) => row.ServiceNotes || "N/A",
      },
    ],
    [],
  );

  const invoicesFilterColumns = useMemo(
    () => [
      { field: "Number", valueGetter: (_, row) => row.Number || "N/A" },
      {
        field: "Client",
        valueGetter: (_, row) => row.Client?.Account || "N/A",
      },
      {
        field: "Date",
        valueGetter: (_, row) =>
          row.Date ? dayjs.tz(row.Date).format("M/D/YYYY") : "N/A",
      },
      {
        field: "Amount",
        valueGetter: (_, row) => {
          const data = row.Amount;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
          }
          return "N/A";
        },
      },
      {
        field: "PaymentMethodID",
        valueGetter: (_, row) =>
          getPaymentMethodDisplayName(row?.Client, row?.PaymentMethodID) || "N/A",
      },
      {
        field: "PurchaseOrder",
        valueGetter: (_, row) => row.PurchaseOrder || "N/A",
      },
      { field: "Note", valueGetter: (_, row) => row.Note || "N/A" },
    ],
    [getPaymentMethodDisplayName],
  );

  // Apply Private Persons filter before column filtering
  const privatePersonsFilteredServices = useMemo(() => {
    if (!listOfServices) return [];
    
    return listOfServices;
  }, [listOfServices, showPrivatePersons]);

  // Column filtering hooks
  const {
    columnFilters: projectsColumnFilters,
    filteredData: projectsFilteredData,
    handleFilterChange: handleProjectsFilterChange,
    clearAllFilters: clearProjectsFilters,
    hasActiveFilters: hasActiveProjectsFilters,
  } = useColumnFilters(
    listOfServices,
    projectsFilterColumns,
  );

  const {
    columnFilters: invoicesColumnFilters,
    filteredData: invoicesFilteredData,
    handleFilterChange: handleInvoicesFilterChange,
    clearAllFilters: clearInvoicesFilters,
    hasActiveFilters: hasActiveInvoicesFilters,
  } = useColumnFilters(listOfInvoices || [], invoicesFilterColumns);

  const fetchServicesData = React.useCallback((monthsInput, yearInput) => {
    setIsLoading(true);
    let months = monthsInput;
    let year = yearInput;

    // Ensure we have valid parameters
    if (!months || months.length === 0) {
      months = [dayjs().format("MMM")];
      // Update both states to reflect the default value
      setSelectedMonths(months);
      setTempSelectedMonths(months);
    }

    if (!year || year.length === 0) {
      year = [String(currentYear)];
      // Update both states to reflect the default value
      setSelectedYear(year);
      setTempSelectedYear(year);
    }

    // Convert months to a comma-separated string if it's an array
    const monthsParam = Array.isArray(months) ? months.join(",") : months;

    api
      .get("/services/all", {
        //to do get all services, and make it light
        params: {
          months: monthsParam,
          year: year[0], // Since year is single-select, we only need the first (and only) value
        },
      })
      .then((response) => {
        if (response !== null) {
          //REVERSE LISTS
          const services = response.data;
          services.reverse();
          setListOfServices(services);
          setListOfInvoices([]);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialogRef.current(message, theme);
        setIsLoading(false);
      });
  }, [currentYear, theme]);

  const parseInvoiceDisplayDate = (value) => {
    const [month, day, year] = String(value)
      .split("/")
      .map((part) => Number(part));

    if (!month || !day || !year) return Number.NaN;
    return dayjs(new Date(year, month - 1, day)).valueOf();
  };

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;

    const firstDate = parseInvoiceDisplayDate(v1);
    const secondDate = parseInvoiceDisplayDate(v2);

    if (Number.isNaN(firstDate)) return 1;
    if (Number.isNaN(secondDate)) return -1;
    return firstDate - secondDate;
  };

  const getProjectsColumns = () => [
    {
      field: "ProjectNumber",
      headerName: "Project #",
      minWidth: 75,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            padding: "8px 0",
          }}
        >
          <Typography>{params.value}</Typography>
        </Box>
      ),
      flex: 0.75,
      renderHeader: () => (
        <DataGridHeader headerName="Project #">
          <ColumnFilter
            field="ProjectNumber"
            data={listOfServices || []}
            onFilterChange={(values) =>
              handleProjectsFilterChange("ProjectNumber", values)
            }
            selectedValues={projectsColumnFilters.ProjectNumber || []}
            openFilter={openProjectsFilter}
            setOpenFilter={setOpenProjectsFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setProjectsSortModel(newSortModel);
              handleProjectsSortModelChange(newSortModel);
            }}
            currentSort={
              projectsSortModel.find((s) => s.field === "ProjectNumber")
                ?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            padding: "8px 0",
          }}
        >
          <Typography>{params.value}</Typography>
        </Box>
      ),
      sortComparator: dateComparator,
      minWidth: 75,
      flex: 0.75,
      renderHeader: () => (
        <DataGridHeader headerName="Date">
          <ColumnDateFilter
            field="Date"
            data={listOfServices || []}
            onFilterChange={(values) =>
              handleProjectsFilterChange("Date", values)
            }
            selectedValues={projectsColumnFilters.Date || []}
            openFilter={openProjectsFilter}
            setOpenFilter={setOpenProjectsFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setProjectsSortModel(newSortModel);
              handleProjectsSortModelChange(newSortModel);
            }}
            currentSort={
              projectsSortModel.find((s) => s.field === "Date")?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data ? data.Account : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            padding: "8px 0",
          }}
        >
          <Typography>{params.value}</Typography>
        </Box>
      ),
      minWidth: 150,
      flex: 1.5,
      renderHeader: () => (
        <DataGridHeader headerName="Client">
          <ColumnFilter
            field="Client"
            data={listOfServices || []}
            onFilterChange={(values) =>
              handleProjectsFilterChange("Client", values)
            }
            selectedValues={projectsColumnFilters.Client || []}
            valueGetter={(_, params) => params.Client?.Account || "N/A"}
            openFilter={openProjectsFilter}
            setOpenFilter={setOpenProjectsFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setProjectsSortModel(newSortModel);
              handleProjectsSortModelChange(newSortModel);
            }}
            currentSort={
              projectsSortModel.find((s) => s.field === "Client")?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => {
        const hasPaidOrInvoiced =
          params.value &&
          (params.value.toUpperCase().includes("PAID") ||
            params.value.toUpperCase().includes("INVOICED"));

        return (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              padding: "8px 0",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                fontWeight: hasPaidOrInvoiced ? "bold" : "normal",
              }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      },
      minWidth: 300,
      flex: 3.0,
      renderHeader: () => (
        <DataGridHeader headerName="Details">
          <ColumnFilter
            field="Details"
            data={listOfServices || []}
            onFilterChange={(values) =>
              handleProjectsFilterChange("Details", values)
            }
            selectedValues={projectsColumnFilters.Details || []}
            openFilter={openProjectsFilter}
            setOpenFilter={setOpenProjectsFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setProjectsSortModel(newSortModel);
              handleProjectsSortModelChange(newSortModel);
            }}
            currentSort={
              projectsSortModel.find((s) => s.field === "Details")?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      minWidth: 80,
      flex: 0.8,
      valueGetter: (_, params) => {
        switch (params.ServiceCategory) {
          case "Interpretation":
            return params?.InterpretationStatus;
          case "Translation":
            return params?.TranslationStatus;
          case "Other":
            return params?.OtherServiceStatus;
          default:
            return "N/A";
        }
      },
      renderCell: (params) => {
        const isNotCompleted = params.value !== "Completed";
        return (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              padding: "8px 0",
            }}
          >
            <Typography
              sx={{
                color: isNotCompleted ? theme.palette.error.main : "inherit",
              }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      },
      renderHeader: () => (
        <DataGridHeader headerName="Status">
          <ColumnFilter
            field="Status"
            data={listOfServices || []}
            onFilterChange={(values) =>
              handleProjectsFilterChange("Status", values)
            }
            selectedValues={projectsColumnFilters.Status || []}
            valueGetter={(_, params) => {
              switch (params.ServiceCategory) {
                case "Interpretation":
                  return params?.InterpretationStatus;
                case "Translation":
                  return params?.TranslationStatus;
                case "Other":
                  return params?.OtherServiceStatus;
                default:
                  return "N/A";
              }
            }}
            openFilter={openProjectsFilter}
            setOpenFilter={setOpenProjectsFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setProjectsSortModel(newSortModel);
              handleProjectsSortModelChange(newSortModel);
            }}
            currentSort={
              projectsSortModel.find((s) => s.field === "Status")?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AccountCharge",
      headerName: "Account Charge",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AccountCharge;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }

        return value;
      },
      renderCell: (params) => {
        const isZeroOrNA =
          params.value === "N/A" || parseFloat(params.row.AccountCharge) === 0;
        return (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              padding: "8px 0",
            }}
          >
            <Typography
              sx={{
                color: isZeroOrNA ? theme.palette.error.main : "inherit",
              }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      },
      minWidth: 100,
      flex: 1.0,
      renderHeader: () => (
        <DataGridHeader headerName="Account Charge">
          <ColumnFilter
            field="AccountCharge"
            data={listOfServices || []}
            onFilterChange={(values) =>
              handleProjectsFilterChange("AccountCharge", values)
            }
            selectedValues={projectsColumnFilters.AccountCharge || []}
            valueGetter={(_, params) => {
              const data = params.AccountCharge;
              if (data !== undefined && data !== null) {
                return parseFloat(data) === 0
                  ? "N/A"
                  : `$${parseFloat(data).toLocaleString()}`;
              }
              return "N/A";
            }}
            openFilter={openProjectsFilter}
            setOpenFilter={setOpenProjectsFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setProjectsSortModel(newSortModel);
              handleProjectsSortModelChange(newSortModel);
            }}
            currentSort={
              projectsSortModel.find((s) => s.field === "AccountCharge")
                ?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceCategory",
      headerName: "Service Category",
      minWidth: 100,
      flex: 1.0,
      renderHeader: () => (
        <DataGridHeader headerName="Service Category">
          <ColumnFilter
            field="ServiceCategory"
            data={listOfServices || []}
            onFilterChange={(values) =>
              handleProjectsFilterChange("ServiceCategory", values)
            }
            selectedValues={projectsColumnFilters.ServiceCategory || []}
            openFilter={openProjectsFilter}
            setOpenFilter={setOpenProjectsFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setProjectsSortModel(newSortModel);
              handleProjectsSortModelChange(newSortModel);
            }}
            currentSort={
              projectsSortModel.find((s) => s.field === "ServiceCategory")
                ?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceNotes",
      headerName: "Notes",
      sortable: false,
      minWidth: 40,
      flex: 0.4,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <IconButton
              sx={{
                color: theme.palette.primary.main,
              }}
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // 👈 prevents DataGrid row click
                handleOpenNotesDialog(params.value);
              }}
              disabled={
                Array.isArray(params.value) && params.value.length === 0
              }
              aria-label="view-notes"
            >
              <NotesRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
      renderHeader: () => (
        <DataGridHeader headerName="Notes">
          <ColumnFilter
            field="ServiceNotes"
            data={listOfServices || []}
            onFilterChange={(values) =>
              handleProjectsFilterChange("ServiceNotes", values)
            }
            selectedValues={projectsColumnFilters.ServiceNotes || []}
            openFilter={openProjectsFilter}
            setOpenFilter={setOpenProjectsFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setProjectsSortModel(newSortModel);
              handleProjectsSortModelChange(newSortModel);
            }}
            currentSort={
              projectsSortModel.find((s) => s.field === "ServiceNotes")?.sort ||
              null
            }
          />
        </DataGridHeader>
      ),
    },
  ];

  const handleRemoveInvoice = (invoice) => {
    // Get the service IDs from this invoice
    const serviceIds = invoice.IDs || [];

    // Find the services that belong to this invoice from listOfInvoicedProjects
    const servicesToRestore = listOfInvoicedProjects.filter((service) =>
      serviceIds.includes(service.id),
    );

    // Move them back to listOfServices
    if (servicesToRestore.length > 0) {
      setListOfServices((prev) => [...(prev || []), ...servicesToRestore]);

      // Remove them from listOfInvoicedProjects
      setListOfInvoicedProjects((prev) =>
        prev.filter((service) => !serviceIds.includes(service.id)),
      );
    }

    // Remove the invoice from the list
    setListOfInvoices((prev) => {
      const updatedInvoices = prev.filter((inv) => inv.Number !== invoice.Number);
      const { updatedNextInvoiceNumber } = renumberDraftInvoices(updatedInvoices);
      setNextInvoiceNumber(updatedNextInvoiceNumber);
      return updatedInvoices;
    });
  };

  const ActionsCell = ({ params }) => {
    const [openPicker, setOpenPicker] = useState(false);
    const anchorRef = useRef(null);

    const handleDateChange = (newValue) => {
      const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
      const dueDate = newValue
        ? newValue.add(30, "day").format("YYYY-MM-DD")
        : "";

      setListOfInvoices((prev) =>
        prev.map((inv) =>
          inv.Number === params.row.Number
            ? { ...inv, Date: formattedDate, DueDate: dueDate }
            : inv,
        ),
      );

      setOpenPicker(false);
    };

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          gap: 0.5,
        }}
      >
        <IconButton
          color="primary"
          size="small"
          ref={anchorRef}
          onClick={(e) => {
            e.stopPropagation();
            setOpenPicker(true);
          }}
        >
          <CalendarMonthRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton
          color="error"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveInvoice(params.row);
          }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
        <DatePicker
          open={openPicker}
          onClose={() => setOpenPicker(false)}
          value={params.row.Date ? dayjs(params.row.Date) : null}
          onChange={handleDateChange}
          slotProps={{
            textField: { sx: { display: "none" } },
            popper: {
              anchorEl: anchorRef.current,
              placement: "bottom-start",
            },
          }}
        />
      </Box>
    );
  };

  const getInvoicesColumns = () => [
    {
      field: "Number",
      headerName: "Invoice #",
      minWidth: 75,
      valueGetter: (_, params) => {
        const data = params.Number;
        const value = data ? data : "N/A";
        return value;
      },
      flex: 0.95,
      renderHeader: () => (
        <DataGridHeader headerName="Invoice N°">
          <ColumnFilter
            field="Number"
            data={listOfInvoices || []}
            onFilterChange={(values) =>
              handleInvoicesFilterChange("Number", values)
            }
            selectedValues={invoicesColumnFilters.Number || []}
            openFilter={openInvoicesFilter}
            setOpenFilter={setOpenInvoicesFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setInvoicesSortModel(newSortModel);
              handleInvoicesSortModelChange(newSortModel);
            }}
            currentSort={
              invoicesSortModel.find((s) => s.field === "Number")?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client?.Account;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.0,
      renderHeader: () => (
        <DataGridHeader headerName="Client">
          <ColumnFilter
            field="Client"
            data={listOfInvoices || []}
            onFilterChange={(values) =>
              handleInvoicesFilterChange("Client", values)
            }
            selectedValues={invoicesColumnFilters.Client || []}
            valueGetter={(_, params) => params.Client?.Account || "N/A"}
            openFilter={openInvoicesFilter}
            setOpenFilter={setOpenInvoicesFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setInvoicesSortModel(newSortModel);
              handleInvoicesSortModelChange(newSortModel);
            }}
            currentSort={
              invoicesSortModel.find((s) => s.field === "Client")?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "PaymentMethodID",
      headerName: "Payment Method",
      minWidth: 170,
      flex: 1.2,
      editable: true,
      valueGetter: (_, params) => params.PaymentMethodID || null,
      renderEditCell: (params) => {
        const paymentMethods = getClientPaymentMethods(params.row?.Client);

        return (
          <TextField
            select
            size="small"
            fullWidth
            value={params.value || ""}
            onChange={(event) => {
              const value = event.target.value;
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: value === "" ? null : Number(value),
              });
            }}
          >
            <MenuItem value="">
              <em>No Method</em>
            </MenuItem>
            {paymentMethods.map((method) => {
              const methodId = method?.PaymentMethodID || method?.PaymentMethod?.id;
              const methodName = method?.PaymentMethod?.Name || `Method ${methodId}`;

              return (
                <MenuItem key={methodId} value={methodId}>
                  {methodName}
                </MenuItem>
              );
            })}
          </TextField>
        );
      },
      renderCell: (params) => {
        const methodName = getPaymentMethodDisplayName(
          params.row?.Client,
          params.row?.PaymentMethodID,
        );

        return (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              padding: "8px 0",
            }}
          >
            <Typography
              sx={{
                color: !methodName ? theme.palette.text.secondary : "inherit",
                fontStyle: !methodName ? "italic" : "normal",
              }}
            >
              {methodName || "Double Click to select Payment Method"}
            </Typography>
          </Box>
        );
      },
      renderHeader: () => (
        <DataGridHeader headerName="Payment Method">
          <ColumnFilter
            field="PaymentMethodID"
            data={listOfInvoices || []}
            onFilterChange={(values) =>
              handleInvoicesFilterChange("PaymentMethodID", values)
            }
            selectedValues={invoicesColumnFilters.PaymentMethodID || []}
            valueGetter={(_, params) =>
              getPaymentMethodDisplayName(params.Client, params.PaymentMethodID) ||
              "N/A"
            }
            openFilter={openInvoicesFilter}
            setOpenFilter={setOpenInvoicesFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setInvoicesSortModel(newSortModel);
              handleInvoicesSortModelChange(newSortModel);
            }}
            currentSort={
              invoicesSortModel.find((s) => s.field === "PaymentMethodID")
                ?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 75,
      flex: 0.75,
      editable: true,
      renderEditCell: (params) => {
        const currentValue = params.value ? dayjs(params.value) : null;
        return (
          <DatePicker
            value={currentValue}
            onChange={(newValue) => {
              const formattedDate = newValue
                ? newValue.format("YYYY-MM-DD")
                : "";
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: formattedDate,
              });

              if (newValue) {
                const dueDate = newValue.add(30, "day").format("YYYY-MM-DD");
                params.api.setEditCellValue({
                  id: params.id,
                  field: "DueDate",
                  value: dueDate,
                });
              }
            }}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        );
      },
      renderHeader: () => (
        <DataGridHeader headerName="Date">
          <ColumnDateFilter
            field="Date"
            data={listOfInvoices || []}
            onFilterChange={(values) =>
              handleInvoicesFilterChange("Date", values)
            }
            selectedValues={invoicesColumnFilters.Date || []}
            openFilter={openInvoicesFilter}
            setOpenFilter={setOpenInvoicesFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setInvoicesSortModel(newSortModel);
              handleInvoicesSortModelChange(newSortModel);
            }}
            currentSort={
              invoicesSortModel.find((s) => s.field === "Date")?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Amount",
      headerName: "Amount",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Amount;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }

        return value;
      },
      minWidth: 75,
      flex: 0.75,
      renderHeader: () => (
        <DataGridHeader headerName="Amount">
          <ColumnFilter
            field="Amount"
            data={listOfInvoices || []}
            onFilterChange={(values) =>
              handleInvoicesFilterChange("Amount", values)
            }
            selectedValues={invoicesColumnFilters.Amount || []}
            valueGetter={(_, params) => {
              const data = params.Amount;
              if (data !== undefined && data !== null) {
                return parseFloat(data) === 0
                  ? "N/A"
                  : `$${parseFloat(data).toLocaleString()}`;
              }
              return "N/A";
            }}
            openFilter={openInvoicesFilter}
            setOpenFilter={setOpenInvoicesFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setInvoicesSortModel(newSortModel);
              handleInvoicesSortModelChange(newSortModel);
            }}
            currentSort={
              invoicesSortModel.find((s) => s.field === "Amount")?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "PurchaseOrder",
      headerName: "Purchase Order",
      valueGetter: (_, params) => {
        const data = params.PurchaseOrder;
        return data || "";
      },
      minWidth: 150,
      flex: 1.5,
      editable: true,
      renderEditCell: (params) => (
        <textarea
          value={params.value || ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            outline: "none",
            padding: "8px",
            fontFamily: "inherit",
            fontSize: "inherit",
            resize: "none",
            backgroundColor: "transparent",
          }}
          placeholder="Press Shift+Enter for new line"
        />
      ),
      renderCell: (params) => (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            padding: "8px 0",
          }}
        >
          <Typography
            sx={{
              color: !params.value ? theme.palette.text.secondary : "inherit",
              fontStyle: !params.value ? "italic" : "normal",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {params.value || "Double Click to add Purchase Order"}
          </Typography>
        </Box>
      ),
      renderHeader: () => (
        <DataGridHeader headerName="Purchase Order">
          <ColumnFilter
            field="PurchaseOrder"
            data={listOfInvoices || []}
            onFilterChange={(values) =>
              handleInvoicesFilterChange("PurchaseOrder", values)
            }
            selectedValues={invoicesColumnFilters.PurchaseOrder || []}
            openFilter={openInvoicesFilter}
            setOpenFilter={setOpenInvoicesFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setInvoicesSortModel(newSortModel);
              handleInvoicesSortModelChange(newSortModel);
            }}
            currentSort={
              invoicesSortModel.find((s) => s.field === "PurchaseOrder")
                ?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Note",
      headerName: "Note",
      minWidth: 150,
      flex: 1.5,
      valueGetter: (_, params) => {
        const data = params.Note;
        return data || "";
      },
      editable: true,
      renderEditCell: (params) => (
        <textarea
          value={params.value || ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            outline: "none",
            padding: "8px",
            fontFamily: "inherit",
            fontSize: "inherit",
            resize: "none",
            backgroundColor: "transparent",
          }}
          placeholder="Press Shift+Enter for new line"
        />
      ),
      renderCell: (params) => (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            padding: "8px 0",
          }}
        >
          <Typography
            sx={{
              color: !params.value ? theme.palette.text.secondary : "inherit",
              fontStyle: !params.value ? "italic" : "normal",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {params.value || "Double Click to add Note"}
          </Typography>
        </Box>
      ),
      renderHeader: () => (
        <DataGridHeader headerName="Note">
          <ColumnFilter
            field="Note"
            data={listOfInvoices || []}
            onFilterChange={(values) =>
              handleInvoicesFilterChange("Note", values)
            }
            selectedValues={invoicesColumnFilters.Note || []}
            openFilter={openInvoicesFilter}
            setOpenFilter={setOpenInvoicesFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setInvoicesSortModel(newSortModel);
              handleInvoicesSortModelChange(newSortModel);
            }}
            currentSort={
              invoicesSortModel.find((s) => s.field === "Note")?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "PaymentLink",
      headerName: "Payment Link",
      minWidth: 150,
      flex: 1.5,
      valueGetter: (_, params) => params.PaymentLink || "",
      editable: true,
      renderEditCell: (params) => {
        const requiresPaymentLink = invoiceRequiresPaymentLink(params.row);

        return (
          <textarea
            disabled={!requiresPaymentLink}
            value={params.value || ""}
            onChange={(e) =>
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: e.target.value,
              })
            }
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              outline: "none",
              padding: "8px",
              fontFamily: "inherit",
              fontSize: "inherit",
              resize: "none",
              backgroundColor: requiresPaymentLink
                ? "transparent"
                : theme.palette.action.disabledBackground,
              color: requiresPaymentLink
                ? "inherit"
                : theme.palette.text.disabled,
            }}
            placeholder={
              requiresPaymentLink
                ? "Add payment link"
                : "Not required for this client"
            }
          />
        );
      },
      renderCell: (params) => {
        const requiresPaymentLink = invoiceRequiresPaymentLink(params.row);

        if (!requiresPaymentLink) {
          return (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                padding: "8px 0",
              }}
            >
              <Typography color="text.secondary" fontStyle="italic">
                Not required
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              padding: "8px 0",
            }}
          >
            <Typography
              sx={{
                color: !params.value ? theme.palette.text.secondary : "inherit",
                fontStyle: !params.value ? "italic" : "normal",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {params.value || "Double Click to add Payment Link"}
            </Typography>
          </Box>
        );
      },
      renderHeader: () => (
        <DataGridHeader headerName="Payment Link">
          <ColumnFilter
            field="PaymentLink"
            data={listOfInvoices || []}
            onFilterChange={(values) =>
              handleInvoicesFilterChange("PaymentLink", values)
            }
            selectedValues={invoicesColumnFilters.PaymentLink || []}
            openFilter={openInvoicesFilter}
            setOpenFilter={setOpenInvoicesFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setInvoicesSortModel(newSortModel);
              handleInvoicesSortModelChange(newSortModel);
            }}
            currentSort={
              invoicesSortModel.find((s) => s.field === "PaymentLink")?.sort ||
              null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 75,
      flex: 0.75,
      sortable: false,
      renderCell: (params) => <ActionsCell params={params} />,
    },
  ];

  const { refreshPage } = useStateContext();

  useEffect(() => {
    // Initial data fetch (with selectedMonths and selectedYear)
    fetchServicesData(selectedMonths, selectedYear);

    Promise.all([
      api.get("/invoices/latestInvoiceNumber"),
      api.get("/invoices/numbers", {
        params: {
          minNumber: INVOICE_NUMBER_REFILL_START,
        },
      }),
    ])
      .then(([latestResponse, numbersResponse]) => {
        const latestNumber = latestResponse.data.latestNumber;
        const persistedNumbers = Array.isArray(numbersResponse.data?.numbers)
          ? numbersResponse.data.numbers
          : [];

        setExistingInvoiceNumbers(persistedNumbers);
        setNextInvoiceNumber(latestNumber ? latestNumber + 1 : 1);
      })
      .catch((error) => {
        console.error("Error fetching invoice number metadata:", error);
        setExistingInvoiceNumbers([]);
        setNextInvoiceNumber(1);
      });
  }, [refreshPage, fetchServicesData, selectedMonths, selectedYear]);

  const extractInvoiceNumbers = useCallback((source = []) => {
    return source
      .map((entry) =>
        typeof entry === "number" ? entry : Number.parseInt(entry?.Number, 10),
      )
      .filter((number) => !Number.isNaN(number));
  }, []);

  const getNextAvailableInvoiceNumber = useCallback(
    (source, startingNumber) => {
      const usedNumbers = extractInvoiceNumbers(source);

      if (usedNumbers.length === 0) {
        return startingNumber;
      }

      const usedNumbersSet = new Set(usedNumbers);
      const highestUsedNumber = Math.max(...usedNumbers);
      const nextSequentialNumber = Math.max(
        startingNumber,
        highestUsedNumber + 1,
      );

      for (
        let candidate = INVOICE_NUMBER_REFILL_START;
        candidate < nextSequentialNumber;
        candidate += 1
      ) {
        if (!usedNumbersSet.has(candidate)) {
          return candidate;
        }
      }

      return nextSequentialNumber;
    },
    [extractInvoiceNumbers],
  );

  const renumberDraftInvoices = useCallback(
    (draftInvoices) => {
      const numberedInvoices = [];

      draftInvoices.forEach((invoice) => {
        const nextNumber = getNextAvailableInvoiceNumber(
          [...existingInvoiceNumbers, ...numberedInvoices],
          nextInvoiceNumber,
        );

        numberedInvoices.push({
          ...invoice,
          Number: nextNumber,
        });
      });

      const updatedNextInvoiceNumber = getNextAvailableInvoiceNumber(
        [...existingInvoiceNumbers, ...numberedInvoices],
        nextInvoiceNumber,
      );

      return {
        numberedInvoices,
        updatedNextInvoiceNumber,
      };
    },
    [existingInvoiceNumbers, getNextAvailableInvoiceNumber, nextInvoiceNumber],
  );

  const handleAddAllInvoices = () => {
    if (!listOfServices || listOfServices.length === 0) {
      console.warn("⚠️ No services found — nothing to invoice.");
      return;
    }

    // 🧠 Group all services by client id
    const groupedByClient = {};
    listOfServices.forEach((service) => {
      const clientId = service.Client?.id || "no_client";
      if (!groupedByClient[clientId]) groupedByClient[clientId] = [];
      groupedByClient[clientId].push(service);
    });

    const newInvoices = [];
    let currentInvoiceNumber = getNextAvailableInvoiceNumber(
      [...existingInvoiceNumbers, ...listOfInvoices],
      nextInvoiceNumber,
    );

    // Build all invoices first (no state updates yet)
    Object.entries(groupedByClient).forEach(
      ([clientId, servicesForClient], i) => {
        const client = servicesForClient[0]?.Client || {
          id: clientId,
          Name: "Unknown Client",
        };

        const totalAmount = servicesForClient.reduce((sum, service) => {
          const charge = parseFloat(service.AccountCharge) || 0;
          return sum + charge;
        }, 0);

        const projects = servicesForClient.map((s) => s.ProjectNumber);
        const ids = servicesForClient.map((s) => s.id);

        // Calculate ServiceDate from earliest service date
        const serviceDates = servicesForClient
          .map((s) => s.Date)
          .filter((date) => date)
          .map((date) => dayjs(date));

        const serviceDate =
          serviceDates.length > 0
            ? serviceDates
                .reduce((earliest, current) =>
                  current.isBefore(earliest) ? current : earliest,
                )
                .format("YYYY-MM-DD")
            : dayjs().format("YYYY-MM-DD");

        const newInvoice = {
          Number: currentInvoiceNumber,
          Client: client,
          Date: dayjs().format("YYYY-MM-DD"),
          ServiceDate: serviceDate,
          DueDate: dayjs().add(30, "day").format("YYYY-MM-DD"),
          Amount: totalAmount,
          Projects: projects,
          IDs: ids,
          PaymentLink: "",
          ...buildInvoicePaymentDefaults(client),
        };

        newInvoices.push(newInvoice);
        // Get the next available number for the next invoice
        currentInvoiceNumber = getNextAvailableInvoiceNumber(
          [...existingInvoiceNumbers, ...listOfInvoices, ...newInvoices],
          nextInvoiceNumber,
        );
      },
    );

    // ✅ Now perform single React state updates
    setListOfInvoices((prev) => {
      const updated = [...prev, ...newInvoices];
      return updated;
    });

    // Move all services to listOfInvoicedProjects
    setListOfInvoicedProjects((prev) => [...prev, ...listOfServices]);

    setListOfServices([]); // since all have been invoiced

    setNextInvoiceNumber(currentInvoiceNumber);

    setSelectedServices([]);

    setTabValue(1);
  };

  const handleAddInvoicesFromSelection = () => {
    if (!selectedServices || selectedServices.length === 0) {
      return;
    }

    // 🧠 Group selected services by client id
    const groupedByClient = {};
    selectedServices.forEach((service) => {
      const clientId = service.Client?.id || "no_client";
      if (!groupedByClient[clientId]) groupedByClient[clientId] = [];
      groupedByClient[clientId].push(service);
    });

    const newInvoices = [];
    let currentInvoiceNumber = getNextAvailableInvoiceNumber(
      [...existingInvoiceNumbers, ...listOfInvoices],
      nextInvoiceNumber,
    );

    Object.entries(groupedByClient).forEach(
      ([clientId, servicesForClient], i) => {
        const client = servicesForClient[0]?.Client || {
          id: clientId,
          Name: "Unknown Client",
        };

        const totalAmount = servicesForClient.reduce((sum, service) => {
          const charge = parseFloat(service.AccountCharge) || 0;
          return sum + charge;
        }, 0);

        const projects = servicesForClient.map((s) => s.ProjectNumber);
        const ids = servicesForClient.map((s) => s.id);

        // Calculate date range from services
        const serviceDates = servicesForClient
          .map((s) => s.Date)
          .filter((date) => date)
          .map((date) => dayjs(date));

        const serviceDate =
          serviceDates.length > 0
            ? serviceDates
                .reduce((earliest, current) =>
                  current.isBefore(earliest) ? current : earliest,
                )
                .format("YYYY-MM-DD")
            : dayjs().format("YYYY-MM-DD");

        const newInvoice = {
          Number: currentInvoiceNumber,
          Client: client,
          Date: dayjs().format("YYYY-MM-DD"),
          ServiceDate: serviceDate,
          DueDate: dayjs().add(30, "day").format("YYYY-MM-DD"), // Default due date 30 days later
          Amount: totalAmount,
          Projects: projects,
          IDs: ids,
          PaymentLink: "",
          ...buildInvoicePaymentDefaults(client),
        };

        newInvoices.push(newInvoice);
        // Get the next available number for the next invoice
        currentInvoiceNumber = getNextAvailableInvoiceNumber(
          [...existingInvoiceNumbers, ...listOfInvoices, ...newInvoices],
          nextInvoiceNumber,
        );
      },
    );

    // ✅ Update state in one go
    setListOfInvoices((prev) => {
      const updated = [...prev, ...newInvoices];
      return updated;
    });

    // Move the invoiced services to listOfInvoicedProjects
    setListOfInvoicedProjects((prev) => [...prev, ...selectedServices]);

    // Remove only the invoiced services from listOfServices
    setListOfServices((prev) =>
      prev.filter(
        (service) => !selectedServices.some((s) => s.id === service.id),
      ),
    );

    setNextInvoiceNumber(currentInvoiceNumber);

    setSelectedServices([]);

    //setTabValue(1);
  };

  const handleAddInvoice = () => {
    const clients = selectedServices.map((service) => service.Client?.id);
    const uniqueClients = [
      ...new Set(clients.filter((id) => id !== undefined)),
    ];

    if (uniqueClients.length > 1) {
      openDialog({
        theme: theme,
        title: "Different Clients Selected",
        message:
          "Would you like to create one Invoice for all selected Projects, or one for each Client?",
        customContent: null, // only showing a message, no custom JSX

        tertiaryAction: "One for All",
        secondaryAction: "Cancel",
        primaryAction: "One for Each",

        tertiaryOnClick: () => {
          createInvoice();
          closeDialog();
        },

        secondaryOnClick: () => {
          closeDialog();
        },

        primaryOnClick: () => {
          handleAddInvoicesFromSelection();
          closeDialog();
        },
      });

      return;
    }

    // If single client or none — go directly
    createInvoice();
  };

  const createInvoice = () => {
    if (selectedServices.length === 0) return;

    // Determine which client to use
    let selectedClient = null;

    // Count projects per client
    const clientCounts = {};
    selectedServices.forEach((service) => {
      const clientId = service.Client?.id;
      if (clientId) {
        if (!clientCounts[clientId]) {
          clientCounts[clientId] = {
            count: 0,
            client: service.Client,
          };
        }
        clientCounts[clientId].count++;
      }
    });

    // Find client with most projects
    let maxCount = 0;
    Object.values(clientCounts).forEach((item) => {
      if (item.count > maxCount) {
        maxCount = item.count;
        selectedClient = item.client;
      }
    });

    // If no client found or tie, use first selected project's client
    if (!selectedClient) {
      selectedClient = selectedServices[0]?.Client || null;
    }

    // Calculate total amount from AccountCharge
    const totalAmount = selectedServices.reduce((sum, service) => {
      const charge = parseFloat(service.AccountCharge) || 0;
      return sum + charge;
    }, 0);

    const projects = selectedServices.map((service) => service.ProjectNumber);
    const ids = selectedServices.map((service) => service.id);

    // Calculate date range from services
    const serviceDates = selectedServices
      .map((s) => s.Date)
      .filter((date) => date)
      .map((date) => dayjs(date));

    const serviceDate =
      serviceDates.length > 0
        ? serviceDates
            .reduce((earliest, current) =>
              current.isBefore(earliest) ? current : earliest,
            )
            .format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");

    // Get the next available invoice number (fills gaps if any)
    const invoiceNumber = getNextAvailableInvoiceNumber(
      [...existingInvoiceNumbers, ...listOfInvoices],
      nextInvoiceNumber,
    );

    // Create the invoice object
    const newInvoice = {
      Number: invoiceNumber,
      Client: selectedClient,
      Date: dayjs().format("YYYY-MM-DD"),
      ServiceDate: serviceDate,
      DueDate: dayjs().add(30, "day").format("YYYY-MM-DD"), // Default due date 30 days later
      Amount: totalAmount,
      Projects: projects,
      IDs: ids,
      PaymentLink: "",
      ...buildInvoicePaymentDefaults(selectedClient),
    };

    // Add to listOfInvoices
    setListOfInvoices((prev) => [...prev, newInvoice]);

    // Move the invoiced services to listOfInvoicedProjects
    setListOfInvoicedProjects((prev) => [...prev, ...selectedServices]);

    // Remove the invoiced services from listOfServices
    setListOfServices((prev) =>
      prev.filter(
        (service) => !selectedServices.some((s) => s.id === service.id),
      ),
    );

    // Update next invoice number to the next available
    const updatedNextNumber = getNextAvailableInvoiceNumber(
      [...existingInvoiceNumbers, ...listOfInvoices, newInvoice],
      nextInvoiceNumber,
    );
    setNextInvoiceNumber(updatedNextNumber);

    // Clear selection
    setSelectedServices([]);
  };

  const saveInvoices = useCallback(async (event, openExport = false) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (listOfInvoices.length === 0) {
      showSnackbar(
        openExport ? "No invoices to print." : "No invoices to save.",
        theme,
        "warning",
      );
      return;
    }

    if (isSaveBlockedByPaymentLinks) {
      showSnackbar(
        `Payment Link is required for ${invoicesMissingPaymentLinks.length} credit card invoice${
          invoicesMissingPaymentLinks.length > 1 ? "s" : ""
        } before continuing.`,
        theme,
        "error",
      );
      return;
    }

    setIsSavingChanges(true);

    try {
      const { numberedInvoices, updatedNextInvoiceNumber } =
        renumberDraftInvoices(listOfInvoices);

      setListOfInvoices(numberedInvoices);
      setNextInvoiceNumber(updatedNextInvoiceNumber);

      // Prepare invoices data for API
      const invoicesData = numberedInvoices.map((invoice) => ({
        Number: invoice.Number,
        ClientID: invoice.Client?.id,
        Date: invoice.Date,
        DueDate: invoice.DueDate || null,
        ServicesDate: invoice.ServiceDate || invoice.Date, // Use ServiceDate, fall back to Date
        Amount: invoice.Amount,
        Note: invoice.Note || null,
        PurchaseOrder: invoice.PurchaseOrder || null,
        PaymentLink:
          typeof invoice.PaymentLink === "string" && invoice.PaymentLink.trim().length > 0
            ? invoice.PaymentLink.trim()
            : null,
      }));

      let createdInvoices;

      // Use bulk or single endpoint based on number of invoices
      if (invoicesData.length === 1) {
        const response = await api.post("/invoices/createOne", invoicesData[0]);
        createdInvoices = [response.data];
      } else {
        const response = await api.post("/invoices/createBulk", {
          invoices: invoicesData,
        });
        createdInvoices = response.data.invoices;
      }

      // Prepare services data for bulk update
      const servicesData = [];

      numberedInvoices.forEach((invoice) => {
        const createdInvoice = createdInvoices.find(
          (ci) => ci.Number === invoice.Number,
        );

        if (createdInvoice && invoice.IDs && invoice.IDs.length > 0) {
          // Add all services associated with this invoice to the update array
          invoice.IDs.forEach((serviceId) => {
            servicesData.push({
              serviceId: serviceId,
              invoiceId: createdInvoice.id,
            });
          });
        }
      });

      // Update all services with their invoice IDs in one request
      if (servicesData.length > 0) {
        await api.put("/services/updateServiceInvoice", {
          services: servicesData,
          changedBy: currentUser?.id || null,
          changedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        });
      }

      // Emit socket event for real-time updates - notify list viewers of new invoices
      if (socket) {
        socket.emit("entity:saved", {
          modelType: "Invoice",
          recordId: "all",
          user: {
            id: currentUser?.id,
            name: currentUser?.Name || "User",
            imageThumbnail: currentUser?.ImageThumbnail,
          },
        });
      }

      // Hand off to export overlay with the created invoices if requested
      if (openExport) {
        setOverlayData({
          invoices: createdInvoices,
          exportOptions: {},
          returnToInvoiceNew: true,
          invoiceNewState: {
            listOfServices,
            listOfInvoices: numberedInvoices,
            selectedMonths,
            selectedYear,
            nextInvoiceNumber: updatedNextInvoiceNumber,
          },
        });

        setOverlayName(OVERLAY_NAMES.INVOICES_EXPORT);
      } else {
        setOverlayName("");
      }

      triggerPageRefresh(
        invoicesData.length + " New Invoices Created Successfully!",
        theme,
      );
    } catch (error) {
      console.error("Error saving invoices:", error);
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save invoices. Please try again.";
      handleErrorDialog(message, theme);
    } finally {
      setIsSavingChanges(false);
    }
  }, [
    currentUser,
    handleErrorDialog,
    listOfInvoices,
    listOfServices,
    renumberDraftInvoices,
    selectedMonths,
    selectedYear,
    isSaveBlockedByPaymentLinks,
    invoicesMissingPaymentLinks.length,
    setOverlayData,
    setOverlayName,
    showSnackbar,
    socket,
    theme,
    triggerPageRefresh,
  ]);

  /**useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && tabValue === 1) {
        e.preventDefault();
        saveInvoices(e, false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [tabValue, listOfInvoices, saveInvoices]);*/

  return (
    <Box
      bgcolor="#fff"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.alt,
          width: "80%",
          height: "85%",
          minWidth: "600px",
          maxWidth: "1400px",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
          }}
        >
          {/* Left side - Title */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => setOverlayName("")}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {"Create Invoice"}
            </Typography>
          </Box>

          {/* Right side - Tabs */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabsChange}
              aria-label="basic tabs example"
              sx={{ bgcolor: theme.palette.background.alt }}
            >
              <CustomTab label="Projects" {...a11yProps(0)} />
              <CustomTab label="Invoices" {...a11yProps(1)} />
            </Tabs>
          </Box>

          {/* Right side - Options menu 
          <Box
            sx={{
              width: "50%",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <IconButton
              color="primary"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              size="large"
            >
              <MoreVertIcon fontSize="inherit" />
            </IconButton>
          </Box>*/}

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                setShowPrivatePersons(!showPrivatePersons);
              }}
            >
              <Checkbox
                checked={showPrivatePersons}
                onChange={(e) => {
                  e.stopPropagation();
                  setShowPrivatePersons(e.target.checked);
                }}
                sx={{ p: 0, mr: 1 }}
              />
              <ListItemText primary="Show Private Persons services" />
            </MenuItem>
          </Menu>
        </Box>

        <Box
          component="form"
          sx={{
            [`.${gridClasses.cell}`]: {
              color: theme.palette.primary.text,
            },
            [`.${gridClasses.cell}.notAvailable`]: {
              color: theme.palette.primary.text3,
            },
            "::-webkit-scrollbar": {
              width: ".5rem",
            },
            "::-webkit-scrollbar-track": {
              background: theme.palette.background.default,
              m: "1rem",
            },
            "::-webkit-scrollbar-thumb": {
              borderRadius: "12px",
              background: theme.palette.background.alt,
            },
            display: "flex",
            //p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            overflowY: "auto",
            width: "100%",
            pb: "0.5rem",

            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
              height: "90%",
            },
          }}
        >
          <CustomTabPanel
            value={tabValue}
            index={0}
            key="Tab One"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "visible",
              position: "relative",
            }}
          >
            <DataGrid
              loading={isLoading}
              checkboxSelection
              disableDensitySelector
              disableColumnSelector
              disableColumnFilter
              disableColumnMenu
              disableColumnSorting
              onRowSelectionModelChange={handleSelectionChange}
              pageSizeOptions={[20, 50, 100]}
              getRowId={(row) => row.id}
              rows={projectsFilteredData || []}
              columns={getProjectsColumns()}
              sortModel={projectsSortModel}
              rowSelectionModel={openProjectsFilter ? [] : undefined}
              disableRowSelectionOnClick={!!openProjectsFilter}
              getRowHeight={() => "auto"}
              onRowClick={(params, event, details) => {
                if (openProjectsFilter) {
                  setOpenProjectsFilter(null);
                  return;
                }
              }}
              localeText={{
                noRowsLabel:
                  listOfInvoices.length !== 0
                    ? "All current Projects have been added to the Invoices tab."
                    : "No Projects were found for the selected month.",
              }}
              getCellClassName={(params) => {
                return params.value === "✘" ||
                  params.value === "N/A" ||
                  params.value === "0%" ||
                  params.value === "null"
                  ? "notAvailable"
                  : "";
              }}
              slots={{
                toolbar: () => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 0,
                    }}
                  >
                    <GridToolbar
                      printOptions={{ disableToolbarButton: true }}
                      csvOptions={{ disableToolbarButton: true }}
                      showQuickFilter={false}
                    />
                    {hasActiveProjectsFilters && (
                      <Button
                        startIcon={<ClearIcon />}
                        onClick={clearProjectsFilters}
                        variant="outlined"
                        size="small"
                        sx={{
                          mt: 0.5,
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          "&:hover": {
                            borderColor:
                              theme.palette.primary.buttonHoverSelected,
                            backgroundColor:
                              theme.palette.primary.buttonHoverSelected,
                            color: theme.palette.background.alt,
                          },
                        }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Box>
                ),
              }}
              slotProps={{
                row: {
                  //onContextMenu: handleContextMenu,
                  //style: { cursor: "context-menu" },
                },
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              ignoreDiacritics
              density="standard"
              sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                borderColor: theme.palette.background.default,
                borderWidth: "2px",
                borderRadius: "1.5rem",
                backgroundColor: theme.palette.background.default,
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-scrollbarFiller": {
                  backgroundColor: theme.palette.background.alt,
                },
                "& .MuiDataGrid-menuIconButton": {
                  opacity: 1,
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-columnSeparator": {
                  color: theme.palette.primary.main,
                },
                "& .MuiButton-root": {
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                  outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                  {
                    outline: "none !important",
                  },
                "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                  flex: 1,
                },
                "& .MuiTypography-root.MuiTypography-body1": {
                  textAlign: "left !important",
                },
                "& .filter-button": {
                  opacity: 0,
                  transition: "opacity 0.2s",
                },
                "& .MuiDataGrid-columnHeader:hover .filter-button": {
                  opacity: 1,
                },
              }}
            />
          </CustomTabPanel>

          <CustomTabPanel
            value={tabValue}
            index={1}
            key="Tab Two"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "visible",
              position: "relative",
            }}
          >
            {isSaveBlockedByPaymentLinks && (
              <Box
                sx={{
                  mb: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.warning.main}`,
                  backgroundColor: `${theme.palette.warning.main}1A`,
                }}
              >
                <Typography variant="body2" color="warning.main">
                  {`Payment Link is required for ${invoicesMissingPaymentLinks.length} credit card invoice${
                    invoicesMissingPaymentLinks.length > 1 ? "s" : ""
                  } before saving or printing.`}
                </Typography>
              </Box>
            )}
            <DataGrid
              loading={isLoading}
              //checkboxSelection
              disableDensitySelector
              disableColumnSelector
              disableColumnFilter
              disableColumnMenu
              disableColumnSorting
              pageSizeOptions={[20, 50, 100]}
              getRowId={(row) => row.Number}
              rows={invoicesFilteredData || []}
              getRowHeight={() => "auto"}
              localeText={{
                noRowsLabel:
                  "No Invoices added yet, start by adding invoices from the Projects tab.",
              }}
              columns={getInvoicesColumns()}
              sortModel={invoicesSortModel}
              rowSelectionModel={openInvoicesFilter ? [] : undefined}
              disableRowSelectionOnClick={!!openInvoicesFilter}
              isCellEditable={(params) => {
                if (params.field !== "PaymentLink") {
                  return !!params.colDef.editable;
                }
                return invoiceRequiresPaymentLink(params.row);
              }}
              processRowUpdate={(newRow, oldRow) => {
                const noteChanged = (newRow.Note || "") !== (oldRow.Note || "");
                const paymentMethodChanged =
                  newRow.PaymentMethodID !== oldRow.PaymentMethodID;

                const updatedRow = {
                  ...newRow,
                  NoteAutoApplied: noteChanged
                    ? false
                    : newRow.NoteAutoApplied ?? oldRow.NoteAutoApplied ?? false,
                };

                if (paymentMethodChanged) {
                  const currentNote =
                    typeof newRow.Note === "string" ? newRow.Note.trim() : "";

                  if (oldRow.NoteAutoApplied || !currentNote) {
                    const defaultInvoiceNote = getDefaultInvoiceNoteForPaymentMethod(
                      newRow.Client,
                      newRow.PaymentMethodID,
                    );

                    updatedRow.Note = defaultInvoiceNote;
                    updatedRow.NoteAutoApplied = defaultInvoiceNote.length > 0;
                  }
                }

                setListOfInvoices((prev) =>
                  prev.map((invoice) =>
                    invoice.Number === updatedRow.Number ? updatedRow : invoice,
                  ),
                );
                return updatedRow;
              }}
              onRowClick={(params, event, details) => {
                if (openInvoicesFilter) {
                  setOpenInvoicesFilter(null);
                  return;
                }
              }}
              getCellClassName={(params) => {
                return params.value === "✘" ||
                  params.value === "N/A" ||
                  params.value === "0%" ||
                  params.value === "null"
                  ? "notAvailable"
                  : "";
              }}
              slots={{
                toolbar: () => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 0,
                    }}
                  >
                    <GridToolbar
                      printOptions={{ disableToolbarButton: true }}
                      csvOptions={{ disableToolbarButton: true }}
                      showQuickFilter={false}
                    />
                    {hasActiveInvoicesFilters && (
                      <Button
                        startIcon={<ClearIcon />}
                        onClick={clearInvoicesFilters}
                        variant="outlined"
                        size="small"
                        sx={{
                          mt: 0.5,
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          "&:hover": {
                            borderColor:
                              theme.palette.primary.buttonHoverSelected,
                            backgroundColor:
                              theme.palette.primary.buttonHoverSelected,
                            color: theme.palette.background.alt,
                          },
                        }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Box>
                ),
              }}
              slotProps={{
                row: {
                  //onContextMenu: handleContextMenu,
                  //style: { cursor: "context-menu" },
                },
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              ignoreDiacritics
              density="standard"
              sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                borderColor: theme.palette.background.default,
                borderWidth: "2px",
                backgroundColor: theme.palette.background.default,
                borderRadius: "1.5rem",
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },

                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-scrollbarFiller": {
                  backgroundColor: theme.palette.background.alt,
                },
                "& .MuiDataGrid-menuIconButton": {
                  opacity: 1,
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-columnSeparator": {
                  color: theme.palette.primary.main,
                },
                "& .MuiButton-root": {
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                  outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                  {
                    outline: "none !important",
                  },
                "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                  flex: 1,
                },
                "& .MuiTypography-root.MuiTypography-body1": {
                  textAlign: "left !important",
                },
                "& .filter-button": {
                  opacity: 0,
                  transition: "opacity 0.2s",
                },
                "& .MuiDataGrid-columnHeader:hover .filter-button": {
                  opacity: 1,
                },
              }}
            />
          </CustomTabPanel>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
          }}
        >
          <Grid
            size={{ xs: 4 }}
            container
            justifyContent="flex-start"
            alignItems="center"
            sx={{
              height: "100%",
              width: "40%", // Take only 30% of parent width
              marginRight: "auto", // Push it to the left
            }}
          >
            <Box width="100%">
              <CustomDateSelector
                label="Date Range"
                months={months}
                years={
                  yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                }
                selectedMonths={tempSelectedMonths}
                selectedYears={tempSelectedYear}
                onSave={handleDateSelectionSave}
                isMultiMonth={true}
                isMultiYear={false}
                monthGridItemSize={3}
                yearGridItemSize={4}
                showClearFilter={false}
              />
            </Box>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-center",
              width: "30%",
              flexDirection: "row",
            }}
          >
            {tabValue === 0 &&
              listOfServices?.length > 0 &&
              (selectedServices?.length > 0 ? (
                !isMobile ? (
                  <Button
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<AddRoundedIcon />}
                    onClick={handleAddInvoice}
                  >
                    Add
                  </Button>
                ) : (
                  <Tooltip title="Add" placement="top">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={handleAddInvoice}
                        sx={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.background.default,
                          borderRadius: "12px",
                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        }}
                      >
                        <AddRoundedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              ) : !isMobile ? (
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<AddRoundedIcon />}
                  onClick={handleAddAllInvoices}
                >
                  Add All
                </Button>
              ) : (
                <Tooltip title="Add All" placement="top">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleAddAllInvoices}
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.background.default,
                        borderRadius: "12px",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <AddRoundedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "40%",
              flexDirection: "row",
            }}
          >
            {tabValue === 1 && (
              <>
                {!isMobile ? (
                  <>
                    <Button
                      loading={isSavingChanges}
                      disabled={listOfInvoices.length === 0 || isSaveBlockedByPaymentLinks}
                      color="primary"
                      variant="outlined"
                      size="large"
                      endIcon={<PrintRoundedIcon />}
                      onClick={(e) => saveInvoices(e, true)}
                      sx={{
                        borderRadius: "12px",
                      }}
                    >
                      Print PDFs
                    </Button>

                    <Box p="0 .5rem" />

                    <Button
                      loading={isSavingChanges}
                      disabled={listOfInvoices.length === 0 || isSaveBlockedByPaymentLinks}
                      color="primary"
                      variant="contained"
                      size="large"
                      endIcon={<SaveRoundedIcon />}
                      type="submit"
                      onClick={(e) => saveInvoices(e, false)}
                      sx={{
                        borderRadius: "12px",
                      }}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Tooltip title="Print PDFs" placement="top">
                      <IconButton
                        color="primary"
                        disabled={listOfInvoices.length === 0 || isSaveBlockedByPaymentLinks}
                        onClick={(e) =>
                          listOfInvoices.length > 0 && !isSaveBlockedByPaymentLinks && saveInvoices(e, true)
                        }
                        sx={{
                          width: "40px",
                          height: "40px",
                          border: `2px solid ${theme.palette.primary.main}`,
                          borderRadius: "12px",
                          mr: 0.5,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.primary.buttonHoverSelected,
                            borderColor:
                              theme.palette.primary.buttonHoverSelected,
                          },
                          "&.Mui-disabled": {
                            opacity: 0.5,
                            borderColor: theme.palette.action.disabled,
                            color: theme.palette.action.disabled,
                          },
                        }}
                      >
                        <PrintRoundedIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Save" placement="top">
                      <span>
                        <IconButton
                          color="primary"
                          disabled={listOfInvoices.length === 0 || isSaveBlockedByPaymentLinks}
                          onClick={(e) =>
                            listOfInvoices.length > 0 && !isSaveBlockedByPaymentLinks && saveInvoices(e, false)
                          }
                          sx={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.background.default,
                            borderRadius: "12px",
                            "&:hover": {
                              backgroundColor: theme.palette.primary.dark,
                            },
                            "&.Mui-disabled": {
                              backgroundColor:
                                theme.palette.action.disabledBackground,
                              color: theme.palette.action.disabled,
                            },
                          }}
                        >
                          <SaveRoundedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </>
            )}

            {tabValue === 0 &&
              (!isMobile ? (
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  endIcon={<DisabledByDefaultRoundedIcon />}
                  onClick={() => setOverlayName("")}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Discard
                </Button>
              ) : (
                <Tooltip title="Discard" placement="top">
                  <IconButton
                    color="primary"
                    onClick={() => setOverlayName("")}
                    sx={{
                      width: "40px",
                      height: "40px",
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                        borderColor: theme.palette.primary.buttonHoverSelected,
                      },
                    }}
                  >
                    <DisabledByDefaultRoundedIcon />
                  </IconButton>
                </Tooltip>
              ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceNew;
